import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Mail, ArrowRight } from 'lucide-react';
import { useT } from '../i18n';
import { getRef } from '../lib/referral';
import { LANDING_CUISINES } from '../lib/cuisines';

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full rounded-2xl border border-neutral-200 bg-white px-5 py-3.5 text-[15px] focus:outline-none focus:border-neutral-900 transition-colors';

export default function DemoAccessPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const nav = useNavigate();
  const verifyToken = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const vipCode = useMemo(() => new URLSearchParams(window.location.search).get('vip') || '', []);
  const dedCode = useMemo(() => new URLSearchParams(window.location.search).get('d') || '', []);
  const profileParam = useMemo(() => new URLSearchParams(window.location.search).get('profile') || '', []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [returning, setReturning] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifyState, setVerifyState] = useState(verifyToken ? 'verifying' : '');
  const [f, setF] = useState({
    name: localStorage.getItem('kx_welcome_name') || '',
    email: localStorage.getItem('kx_welcome_email') || '',
    restaurant_name: localStorage.getItem('kx_welcome_restaurant') || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = /.+@.+\..+/.test(f.email);
  const [cuisine, setCuisine] = useState(() => {
    try { return profileParam || localStorage.getItem('kx_demo_profile') || ''; } catch { return profileParam || ''; }
  });
  const pickCuisine = (k) => {
    setCuisine(k);
    try { localStorage.setItem('kx_demo_profile', k); } catch { /* noop */ }
  };

  // A shared link carries the friend's cuisine — remember it and apply it once access is granted.
  useEffect(() => {
    if (profileParam) { try { localStorage.setItem('kx_demo_profile', profileParam); } catch { /* noop */ } }
  }, [profileParam]);
  const applyProfile = async () => {
    let p = profileParam;
    try { p = p || localStorage.getItem('kx_demo_profile') || ''; } catch { /* noop */ }
    if (!p) return;
    try {
      await fetch(`${API}/api/rest/demo/enter`, { method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile: p }) });
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!verifyToken) return;
    fetch(`${API}/api/demo-access/verify`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verifyToken }) })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json())?.detail || 'error');
        setVerifyState('ok');
        // Clicking the emailed link IS the invitation — the full show (music + narration) starts again, always.
        try {
          sessionStorage.setItem('kx_present_force', '1');
          localStorage.removeItem('kx_present_progress');
        } catch { /* noop */ }
        await applyProfile();
        setTimeout(() => nav('/restaurant', { replace: true }), 1600);
      })
      .catch(() => setVerifyState('bad'));
  }, [verifyToken, nav]);

  useEffect(() => {
    if (!dedCode) return;
    setVerifyState('verifying');
    fetch(`${API}/api/demo-access/dedication-redeem`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: dedCode }) })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json())?.detail || 'error');
        const d = await r.json();
        setVerifyState('ok');
        try {
          sessionStorage.setItem('kx_present_force', '1');
          localStorage.removeItem('kx_present_progress');
          sessionStorage.setItem('kx_dedication_to', d.to_name || '');
          sessionStorage.setItem('kx_dedication_msg', d.message || '');
          sessionStorage.setItem('kx_dedication_voice', d.voice || 'onyx');
          sessionStorage.setItem('kx_dedication_sign', d.signature || '');
          // One language for the whole journey — dedication, intro and demo speak as one.
          localStorage.setItem('kitxens_lang', (d.locale || 'es').startsWith('es') ? 'es' : 'en');
        } catch { /* noop */ }
        await applyProfile();
        setTimeout(() => window.location.replace('/restaurant'), 1200);
      })
      .catch(() => setVerifyState('bad'));
  }, [dedCode, nav]);

  useEffect(() => {
    if (!vipCode) return;
    setVerifyState('verifying');
    fetch(`${API}/api/demo-access/vip-redeem`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: vipCode, profile: profileParam || '' }) })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json())?.detail || 'error');
        setVerifyState('ok');
        // A shared VIP link IS the experience — it always opens the show from its very first screen.
        try {
          sessionStorage.setItem('kx_present_force', '1');
          localStorage.removeItem('kx_present_progress');
          const dedication = new URLSearchParams(window.location.search).get('to');
          if (dedication) {
            sessionStorage.setItem('kx_dedication_to', dedication);
            // The dedication speaks Spanish — the whole experience follows in the same voice and tongue.
            localStorage.setItem('kitxens_lang', 'es');
          }
        } catch { /* noop */ }
        await applyProfile();
        setTimeout(() => window.location.replace('/restaurant'), 1200);
      })
      .catch(() => setVerifyState('bad'));
  }, [vipCode, nav]);

  const submit = async (confirmReturning) => {
    if (!valid || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/demo-access/request`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, ref_code: getRef() || '', locale: lang, origin: window.location.origin,
          confirm_returning: !!confirmReturning,
          profile: cuisine || profileParam || localStorage.getItem('kx_demo_profile') || '',
          ai_time: localStorage.getItem('kx_ai_time') || '', visitor_path: localStorage.getItem('kx_visitor_path') || '',
          first_interest: localStorage.getItem('kx_first_interest') || '' }) });
      const j = await r.json();
      if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Error');
      if (j.admin_access) { nav('/restaurant'); return; }
      if (j.already_registered) { setReturning(j); return; }
      setReturning(null);
      setSent(true);
      try {
        const { trackEvent } = await import('../lib/analytics');
        trackEvent('schedule_demo', { locale: lang });
      } catch { /* noop */ }
    } catch (e) { setErr(String(e.message)); } finally { setBusy(false); }
  };

  if (verifyState) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6" data-testid="demo-verify-screen">
        <div className="text-center max-w-md">
          {verifyState === 'verifying' && <><div className="mx-auto w-12 h-12 rounded-full border-[3px] border-neutral-200 border-t-[#F77F11] animate-spin" /><p className="mt-5 text-[15px] font-semibold text-neutral-900">{isEs ? 'Verificando tu email…' : 'Verifying your email…'}</p></>}
          {verifyState === 'ok' && <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Check className="w-8 h-8" /></span>
            <p className="mt-5 text-[20px] font-bold text-neutral-900">{isEs ? 'Dentro.' : "You're in."}</p>
            <p className="mt-1 text-[14px] text-neutral-500">{isEs ? 'Abriendo tu demo del Restaurant AI OS…' : 'Opening your Restaurant AI OS demo…'}</p>
          </motion.div>}
          {verifyState === 'bad' && <><p className="text-[18px] font-bold text-neutral-900">{isEs ? 'Enlace no válido o caducado' : 'Invalid or expired link'}</p>
            <button onClick={() => { setVerifyState(''); window.history.replaceState({}, '', '/demo'); }} className="mt-4 rounded-full bg-[#F77F11] text-white font-bold px-6 py-2.5 text-[13.5px]" data-testid="demo-verify-retry">{isEs ? 'Solicitar acceso de nuevo' : 'Request access again'}</button></>}
        </div>
      </div>
    );
  }

  if (returning) {
    const shareUrl = `${window.location.origin}/demo`;
    const shareText = isEs ? `Échale un ojo a la demo del Restaurant AI OS de Kitxens — merece la pena: ${shareUrl}`
      : `Check out the Kitxens Restaurant AI OS demo — it's worth it: ${shareUrl}`;
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6" data-testid="demo-returning">
        <motion.div initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-md">
          <a href="/" className="text-[17px] font-extrabold text-neutral-900">kit<span className="text-[#F77F11]">x</span>ens</a>
          <h1 className="mt-7 text-[26px] sm:text-[30px] font-extrabold text-neutral-900 tracking-tight leading-tight">
            {returning.name ? (isEs ? `${returning.name}, ya nos conocemos` : `${returning.name}, we already know each other`) : (isEs ? 'Ya nos conocemos' : 'We already know each other')}
          </h1>
          <p className="mt-2 text-[14.5px] text-neutral-500 leading-relaxed">
            {isEs ? <>El email <b className="text-neutral-800">{f.email}</b> ya {returning.verified ? 'hizo la demo' : 'está registrado'}. Dinos qué prefieres:</>
              : <>The email <b className="text-neutral-800">{f.email}</b> already {returning.verified ? 'did the demo' : 'is registered'}. Tell us what you prefer:</>}
          </p>
          <div className="mt-7 space-y-2.5">
            <button onClick={() => submit(true)} disabled={busy} data-testid="demo-returning-again"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-7 py-3.5 text-[14px] font-bold text-white hover:bg-neutral-700 transition-colors disabled:opacity-40">
              {busy ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Sí, quiero entrar otra vez' : 'Yes, I want back in')}
            </button>
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" data-testid="demo-returning-share"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 px-7 py-3.5 text-[14px] font-bold text-neutral-700 hover:border-neutral-900 transition-colors">
              {isEs ? 'Recomendárnosla a un colega' : 'Recommend us to a colleague'}
            </a>
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }} data-testid="demo-returning-copy"
              className="w-full text-[12.5px] text-neutral-400 hover:text-neutral-700 transition-colors py-1">
              {copied ? (isEs ? '✓ Enlace copiado' : '✓ Link copied') : (isEs ? 'O copiar el enlace de la demo' : 'Or copy the demo link')}
            </button>
            <button onClick={() => { setReturning(null); setF({ ...f, email: '' }); }} data-testid="demo-returning-other-email"
              className="w-full text-[12.5px] text-neutral-400 hover:text-neutral-700 transition-colors py-1">
              {isEs ? 'Usar otro email' : 'Use another email'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (sent) {
    const first = f.name ? f.name.split(' ')[0] : '';
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6" data-testid="demo-check-email">
        <motion.div initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center max-w-md">
          <span className="mx-auto w-16 h-16 rounded-full bg-[#FFF3E8] text-[#F77F11] flex items-center justify-center"><Mail className="w-7 h-7" /></span>
          <h1 className="mt-5 text-[22px] font-bold text-neutral-900">{first ? (isEs ? `${first}, revisa tu email` : `${first}, check your email`) : (isEs ? 'Revisa tu email' : 'Check your email')}</h1>
          <p className="mt-2 text-[14px] text-neutral-500 leading-relaxed">{isEs ? <>Te hemos enviado un enlace a <b className="text-neutral-800">{f.email}</b>. Confírmalo y entras directo a la demo.</> : <>We sent a link to <b className="text-neutral-800">{f.email}</b>. Confirm it and you go straight into the demo.</>}</p>
          <button data-testid="demo-resend" onClick={async () => { await fetch(`${API}/api/demo-access/resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: f.email }) }); }}
            className="mt-5 text-[12.5px] font-semibold text-[#F77F11] underline">{isEs ? '¿No llegó? Reenviar email' : "Didn't arrive? Resend email"}</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6 py-12" data-testid="demo-wizard">
      <motion.div initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-lg">
        <a href="/" className="text-[17px] font-extrabold text-neutral-900">kit<span className="text-[#F77F11]">x</span>ens</a>
        <h1 className="mt-7 text-[28px] sm:text-[32px] font-extrabold text-neutral-900 tracking-tight leading-tight">
          {isEs ? 'Entra a la demo' : 'Enter the demo'}
        </h1>
        <p className="mt-2 text-[14.5px] text-neutral-500 leading-relaxed">
          {isEs ? 'Tu email y listo — te enviamos el enlace de acceso. El resto nos lo cuentas dentro, si quieres.'
            : 'Just your email — we\u2019ll send you the access link. You can tell us the rest inside, if you want.'}
        </p>
        <div className="mt-7" data-testid="demo-cuisine-picker">
          <p className="text-[20px] sm:text-[22px] font-bold text-neutral-900 tracking-tight leading-snug">
            {isEs ? '¿Qué tipo de restaurante tienes?' : 'What type of restaurant do you operate?'}
          </p>
          <p className="mt-1 text-[13px] text-neutral-500">
            {isEs ? 'Elige tu cocina — toda la demo (menú, ventas y reseñas) se personaliza a tu negocio.' : 'Choose your type — the entire demo (menu, sales and reviews) personalizes to your business.'}
          </p>
          <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {LANDING_CUISINES.map((c) => (
              <button key={c.key} type="button" data-testid={`demo-cuisine-${c.key}`}
                onClick={() => pickCuisine(c.key)} aria-pressed={cuisine === c.key}
                className={`relative rounded-2xl px-3 py-5 text-center transition-all duration-200 hover:-translate-y-0.5 ${cuisine === c.key
                  ? 'bg-[#F77F11] text-white ring-2 ring-[#F77F11] ring-offset-2 ring-offset-[#FAFAF8] shadow-xl'
                  : 'bg-white text-neutral-800 ring-1 ring-neutral-200 hover:ring-[#F77F11]/60 hover:shadow-lg'}`}>
                <span className="text-[16px] sm:text-[17px] font-bold leading-tight tracking-tight">
                  {isEs ? c.es : c.en}
                </span>
                {cuisine === c.key && (
                  <span className="absolute top-2 right-2 w-[18px] h-[18px] rounded-full bg-white text-[#F77F11] text-[11px] font-bold flex items-center justify-center shadow" aria-hidden="true">✓</span>
                )}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-neutral-400">
            {isEs ? 'Puedes cambiar de cocina en cualquier momento dentro de la demo.' : 'You can switch cuisine anytime inside the demo.'}
          </p>
        </div>
        <div className="mt-5 space-y-3">
          <input type="email" autoFocus={!f.email} className={inputCls} placeholder="Email *" value={f.email} onChange={set('email')}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(false); }} data-testid="demo-email" />
          <input className={inputCls} placeholder={isEs ? 'Tu nombre (opcional)' : 'Your name (optional)'} value={f.name} onChange={set('name')}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(false); }} data-testid="demo-name" />
        </div>
        {err && <p className="mt-4 text-[13px] font-semibold text-red-500" data-testid="demo-error">{err}</p>}
        <button onClick={() => submit(false)} disabled={!valid || busy} data-testid="demo-next"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-7 py-3.5 text-[14.5px] font-bold text-white hover:bg-neutral-700 transition-colors disabled:opacity-30">
          {busy ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Recibir mi acceso' : 'Get my access')}
          {!busy && <ArrowRight className="w-4 h-4" />}
        </button>
        <p className="mt-4 text-[11.5px] text-neutral-400">{isEs ? '30 segundos · sin tarjeta · acceso inmediato tras confirmar tu email' : '30 seconds · no card · instant access after confirming your email'}</p>
      </motion.div>
    </div>
  );
}
